import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ITaskModel } from 'src/app/shared/interfaces/task-model.interface';
import { tasksFactory } from './task.factory';
import ITask = ZoningAPI.ITask;
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import * as _ from 'lodash';
import { BaseEntityService } from 'src/app/shared/services/base-entity.service';

const PROCESS_API_ENDPOINT = environment.ZONAR_API_URL + '/process';

export class BaseProcessService extends BaseEntityService<ITaskModel> {
    readonly processesSubject = new BehaviorSubject<ITaskModel[]>([]);
    readonly processes = this.processesSubject.asObservable().pipe(distinctUntilChanged());

    readonly archivedProcessesSubject = new BehaviorSubject<ITaskModel[]>([]);
    readonly archivedProcesses = this.archivedProcessesSubject.asObservable().pipe(distinctUntilChanged());

    readonly processesListChangedSubject = new Subject<number>();
    readonly processesListChanged = this.processesListChangedSubject.asObservable();
    readonly archivedProcessesListChangedSubject = new Subject<number>();
    readonly archivedProcessesListChanged = this.archivedProcessesListChangedSubject.asObservable();

    constructor(protected readonly http: HttpClient, protected readonly snackBar: MatSnackBar) {
        super(tasksFactory);
    }

    getProcessById(processId: string) {
        return this.processesSubject.value.find(process => process.id === processId);
    }

    private getFromObject(
        task: ITask,
        skipStatistic: boolean = false,
        restart?: boolean,
        makeIdle?: boolean,
        getArchived?: boolean
    ) {
        const params: any = {
            fromObject: {
                state_env: task.state,
                skipStatistic: String(skipStatistic)
            }
        };
        if (restart) {
            params.fromObject.restart = String(restart);
        }
        if (makeIdle) {
            params.fromObject.makeIdle = String(makeIdle);
        }
        if (getArchived) {
            params.fromObject.getArchived = String(getArchived);
        }
        return new HttpParams(params);
    }

    private requestWithCommonPipe(task: ITaskModel, request: Observable<any>) {
        if (!task || !request) {
            return of(null);
        }

        return request.pipe(
            catchError(err => {
                console.log(err);
                task.isWaitingActionFinish = false;
                this.snackBar.open(_.get(err, 'error.messages[0]', `Operation with Process ${task.id} failed`), 'OK', {
                    verticalPosition: 'top',
                    horizontalPosition: 'center',
                    duration: 4000
                });
                return throwError(err);
            }),
            map((response: any) => {
                task.isWaitingActionFinish = false;
                return response ? (response.data as ITask) : null;
            })
        );
    }

    continueProcess(task: ITaskModel, skipStatistic: boolean = false): Observable<ITask> {
        const params = this.getFromObject(task, skipStatistic);
        task.isWaitingActionFinish = true;
        return this.requestWithCommonPipe(
            task,
            this.http.get<Response>(`${PROCESS_API_ENDPOINT}/${task.id}/continue`, { params })
        );
    }

    stopProcess(task: ITaskModel, skipStatistic: boolean = false, makeIdle: boolean = false) {
        const params = this.getFromObject(task, skipStatistic, null, makeIdle);
        task.isWaitingActionFinish = true;
        return this.requestWithCommonPipe(
            task,
            this.http.get<Response>(`${PROCESS_API_ENDPOINT}/${task.id}/stop`, { params })
        );
    }

    deleteProcess(task: ITaskModel, skipStatistic: boolean = false) {
        const params = this.getFromObject(task, skipStatistic);
        task.isWaitingActionFinish = true;
        return this.requestWithCommonPipe(
            task,
            this.http.delete<Response>(`${PROCESS_API_ENDPOINT}/${task.id}`, { params })
        );
    }

    updateProcess(task: ITaskModel, partialModel: any, skipStatistic: boolean = false, restart?: boolean) {
        const params = this.getFromObject(task, skipStatistic, restart);

        const headers = new HttpHeaders();
        headers.set('Content-Type', null);
        headers.set('Accept', 'multipart/form-data');
        task.isWaitingActionFinish = true;

        return this.requestWithCommonPipe(
            task,
            this.http.patch<Response>(`${PROCESS_API_ENDPOINT}/${task.id}`, partialModel, {
                params,
                responseType: 'json',
                headers
            })
        );
    }

    uploadProcessById(task: ITaskModel, skipStatistic: boolean = false) {
        const params = this.getFromObject(task, skipStatistic);
        task.isWaitingActionFinish = true;
        return this.requestWithCommonPipe(
            task,
            this.http.get<Response>(`${PROCESS_API_ENDPOINT}/${task.id}`, { params })
        );
    }

    restartProcess(task: ITaskModel, skipStatistic: boolean = false) {
        const params = this.getFromObject(task, skipStatistic);
        task.isWaitingActionFinish = true;
        return this.requestWithCommonPipe(
            task,
            this.http.get<Response>(`${PROCESS_API_ENDPOINT}/${task.id}/restart`, { params })
        );
    }

    clearThreads(task: ITaskModel, skipStatistic: boolean = false) {
        const params = this.getFromObject(task, skipStatistic);
        task.isWaitingActionFinish = true;
        return this.requestWithCommonPipe(
            task,
            this.http.get<Response>(`${PROCESS_API_ENDPOINT}/${task.id}/clearThreads`, { params })
        );
    }

    triggerUpdates() {
        this.processesSubject.next([...this.processesSubject.value]);
    }

    notifyProcessListChanged(notifyArchived: boolean = false) {
        notifyArchived
            ? this.archivedProcessesListChangedSubject.next(Date.now())
            : this.processesListChangedSubject.next(Date.now());
    }

    updateProcessesWithJSON(processes: ITaskModel[], dtos: ITask[]) {
        if (_.isEmpty(processes) || _.isEmpty(dtos)) {
            return;
        }
        dtos.forEach(dto => {
            const existingProcess = processes.find(process => process.id === dto.id);
            if (existingProcess) {
                existingProcess.initWithJSON(dto);
            }
        });
    }

    getExistingProcesses(processes: ITaskModel[], removedProcessesDTOs: ITask[]) {
        if (_.isEmpty(processes) || _.isEmpty(removedProcessesDTOs)) {
            return processes;
        }
        return processes.filter(process => !removedProcessesDTOs.find(dto => process.id === dto.id));
    }
}
